import styles from './add-template-form.module.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  FormTemplate,
  QUESTION_TYPE_VALUES,
  QuestionTypeEnum,
  RadioOption,
  SurveyQuestion,
} from '@employ-engage/data';
import cloneDeep from 'lodash.clonedeep';
import { useState } from 'react';
import 'quill/dist/quill.snow.css';
import { nanoid } from 'nanoid';
import classNames from 'classnames';
import QuestionOptionsForm from '../question-options-form/question-options-form';
import { useAlert } from '../hooks';
import ErrorMessage from '../error-message/error-message';
import Toggle from '../toggle/toggle';
import RichTextEditor from '../rich-text-editor/rich-text-editor';
import Tooltip from '../tooltip/tooltip';
import ConfirmationModal from '../confirmation-modal/confirmation-modal';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../contexts/auth-context-type';
import { useMutation } from '@apollo/client';
import {
  UPDATE_FORM_TEMPLATE,
  CREATE_FORM_TEMPLATE,
  removeFollowingFields,
} from '../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';

export interface AddTemplateFormProps {
  onClose: () => void;
  templateSelectedForEditing: FormTemplate | null;
  refetchFormTemplates: () => void;
}

export function AddTemplateForm({
  onClose,
  templateSelectedForEditing,
  refetchFormTemplates,
}: AddTemplateFormProps) {
  const { t } = useTranslation(['common']);
  const [shouldCloseAfterSave, setShouldCloseAfterSave] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // TODO: Add loading spinner
  const [pageToDelete, setPageToDelete] = useState(-1);
  const { user } = useAuthUser();
  const [createFormTemplate] = useMutation(CREATE_FORM_TEMPLATE);
  const [updateFormTemplate, { loading: isSavingTemplate }] =
    useMutation(UPDATE_FORM_TEMPLATE);
  const { Alert: TemplateSavedAlert, fireAlert } = useAlert({
    title: t('Template Saved'),
    message: t('Your template has been saved.'),
    type: 'success',
  });

  const saveTemplate = (values: FormTemplate) => {
    const val = { ...values, ownerId: user?.id };
    val.changesPublished = false;
    if (templateSelectedForEditing?.id) {
      return updateFormTemplate({
        variables: {
          input: {
            ...val,
          },
          id: templateSelectedForEditing.id,
        },
      });
    }
    return createFormTemplate({
      variables: {
        input: {
          ...val,
        },
      },
    });
  };

  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false);
  const formik = useFormik({
    initialValues: templateSelectedForEditing || {
      name: '',
      isAnonymous: false,
      isInfinitelyRespondable: false,
      isPasscodeRequired: false,
      welcomePageRichText: {},
      completionButtonLabel: t('Complete Form'),
      pages: [
        {
          welcomeText: {},
          questions: [
            {
              id: nanoid(),
              question: '',
              type: QuestionTypeEnum.SHORT_TEXT,
              options: null,
              isRequired: true,
            },
          ] as SurveyQuestion[],
        },
      ],
    },
    onSubmit: (values) => {
      setIsSaving(true);
      const cleanedValues = removeFollowingFields(values, ['__typename']);
      saveTemplate(cleanedValues as FormTemplate)
        .then(() => {
          fireAlert(5000);
          if (shouldCloseAfterSave) {
            formik.resetForm();
            onClose();
            setShouldCloseAfterSave(false);
          }
          setIsSaving(false);
          refetchFormTemplates();
        })
        .catch((error) => {
          console.error(error);
        });
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required(t('Required')),
      pages: Yup.array().of(
        Yup.object().shape({
          questions: Yup.array().of(
            Yup.object().shape({
              question: Yup.string().required(t('Required')),
              // `required()` alone accepts any string at all, which is how a
              // type the app cannot render reached the database.
              type: Yup.string()
                .required(t('Required'))
                .oneOf([...QUESTION_TYPE_VALUES], t('Unsupported question type')),
              // A scale whose top is below its bottom offers no valid answer,
              // so a required question using one could never be submitted —
              // the same trap an unrenderable question type used to set.
              scale: Yup.object()
                .shape({
                  min: Yup.number(),
                  max: Yup.number(),
                  direction: Yup.string(),
                })
                .test(
                  'range',
                  t('The highest number must be above the lowest'),
                  (scale) =>
                    !scale ||
                    scale.min === undefined ||
                    scale.max === undefined ||
                    scale.max > scale.min
                )
                .test(
                  'length',
                  t('A scale can have at most 11 points'),
                  (scale) =>
                    !scale ||
                    scale.min === undefined ||
                    scale.max === undefined ||
                    scale.max - scale.min <= 10
                )
                .nullable()
                .optional(),
            })
          ),
        })
      ),
    }),
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    errors,
    touched,
    setFieldValue,
    dirty,
  } = formik;

  const calculateQuestionNumber = (
    pageIndex: number,
    questionIndex: number
  ) => {
    let questionNumber = 1;
    for (let i = 0; i < pageIndex; i++) {
      questionNumber += values.pages[i].questions.length;
    }
    questionNumber += questionIndex;
    return questionNumber;
  };

  // Reporting identifies a question by this id, not by its wording, so that
  // rewording a question keeps its answer history in one series. Publishing a
  // version deep-clones these pages, which is what carries the id forward.
  // If question reordering is ever added here, it must move the whole question
  // object (id included) — reports fall back to position when an id is missing,
  // and a reorder that dropped ids would silently mix up two questions' answers.
  const addQuestion = (pageIndex: number) => {
    const newQuestion: {
      id: string;
      question: string;
      type: QuestionTypeEnum;
      options?: RadioOption[];
      isColumn?: boolean;
      isRequired?: boolean;
    } = {
      id: nanoid(),
      question: '',
      type: QuestionTypeEnum.SHORT_TEXT,
      isRequired: true,
    };
    const newPages = [...values.pages];
    newPages[pageIndex].questions.push(newQuestion as any);
    setFieldValue('pages', newPages);
  };

  const addPage = () => {
    const newPage = {
      welcomeText: {},
      questions: [
        {
          id: nanoid(),
          question: '',
          type: QuestionTypeEnum.SHORT_TEXT,
        },
      ],
    };
    setFieldValue('pages', [...values.pages, newPage]);
  };

  const deletePage = (pageIndex: number) => {
    const newPages = [...values.pages];
    newPages.splice(pageIndex, 1);
    setFieldValue('pages', newPages);
  };

  const deleteQuestion = (pageIndex: number, questionIndex: number) => {
    const newPages = cloneDeep(values.pages);
    newPages[pageIndex].questions.splice(questionIndex, 1);
    setFieldValue('pages', newPages);
  };

  const handleTypeChange = (
    e: any,
    pageIndex: number,
    questionIndex: number
  ) => {
    if (e.target.value === 'select one' || e.target.value === 'select many') {
      setFieldValue(`pages[${pageIndex}].questions[${questionIndex}].options`, [
        { id: nanoid(), label: '' },
        { id: nanoid(), label: '' },
      ]);
    } else {
      setFieldValue(
        `pages[${pageIndex}].questions[${questionIndex}].options`,
        null,
        true
      );
      // A scale is meaningless without a range, and asking the author to set
      // one before the question works would be a trap. 1-5 is the common case
      // and they can change it.
      if (e.target.value === 'scale') {
        setFieldValue(`pages[${pageIndex}].questions[${questionIndex}].scale`, {
          min: 1,
          max: 5,
          direction: 'higherIsBetter',
        });
      }
    }

    handleChange(e);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 relative block">
      <div
        className={classNames(
          'overflow-y-auto space-y-4 box-content',
          styles['add-template-form']
        )}
      >
        <div className={classNames('space-y-4', styles['inner-container'])}>
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <div className="flex flex-col">
                <label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-700"
                >
                  {t('Name')}
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                />
                {errors.name && touched.name && (
                  <ErrorMessage errors={errors.name} touched={touched.name} />
                )}
              </div>
            </div>
            <div className="col-span-2">
              <div className="flex flex-col space-y-2.5">
                <label
                  htmlFor="isAnonymous"
                  className="text-sm font-medium text-gray-700"
                >
                  {t('Anonymous?')}
                </label>
                <Toggle
                  id="isAnonymous"
                  label={t('Should this form be anonymous?')}
                  enabled={values.isAnonymous}
                  setEnabled={(isAnonymous) =>
                    setFieldValue('isAnonymous', isAnonymous)
                  }
                />
              </div>
            </div>
            <div className="col-span-2">
              <div className="flex flex-col space-y-2.5">
                <label
                  htmlFor="isAnonymous"
                  className="text-sm font-medium text-gray-700"
                >
                  {t('Infinite Responses?')}
                </label>
                <Toggle
                  id="isInfinitelyRespondable"
                  label={t('Allow infinite responses per employee?')}
                  enabled={values.isInfinitelyRespondable}
                  setEnabled={(isInfinitelyRespondable) =>
                    setFieldValue(
                      'isInfinitelyRespondable',
                      isInfinitelyRespondable
                    )
                  }
                />
              </div>
            </div>
            <div className="col-span-2">
              <div className="flex flex-col space-y-2.5">
                <label
                  htmlFor="isPasscodeRequired"
                  className="text-sm font-medium text-gray-700"
                >
                  {t('Require passcode?')}
                </label>
                <Toggle
                  id="isPasscodeRequired"
                  label="Require passcode to take survey?"
                  enabled={values.isPasscodeRequired}
                  setEnabled={(isPasscodeRequired) =>
                    setFieldValue('isPasscodeRequired', isPasscodeRequired)
                  }
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col col-span-2">
            <label
              htmlFor="welcomePageRichText"
              className="text-sm font-medium text-gray-700"
            >
              {t('Welcome Page')}
            </label>
            <RichTextEditor
              id="welcomePageRichText"
              formik={formik}
              value={values.welcomePageRichText}
            />
            <ErrorMessage
              errors={
                (errors?.welcomePageRichText as any)?.ops?.[0]?.insert?.length
              }
              touched={touched.pages}
            />
          </div>

          {values.pages.map((page, pageIndex, pages) => {
            return (
              <fieldset
                className="rounded-md border-2 border-gray-400 p-2 relative"
                key={`page-${pageIndex}`}
              >
                {pages.length > 1 && (
                  <div className="absolute -top-[26px] -right-[13px] bg-white rounded-full w-[28px] flex justify-center">
                    <Tooltip
                      label={t('Delete Page', { pageNumber: pageIndex + 1 })}
                    >
                      <button
                        onClick={() => {
                          setPageToDelete(pageIndex);
                          setIsConfirmDeleteModalOpen(true);
                        }}
                      >
                        <i className="fa-solid fa-circle-xmark text-red-500 text-lg cursor-pointer border border-white p-1 rounded-full" />
                      </button>
                    </Tooltip>
                  </div>
                )}
                <legend className="text-base font-bold text-gray-500 px-1">
                  {t('Page', { pageNumber: pageIndex + 1 })}
                </legend>
                <div className="mx-4">
                  <label
                    htmlFor={`pages[${pageIndex}].welcomeText`}
                    className="text-sm font-medium text-gray-700"
                  >
                    {t('Welcome Text')}
                  </label>
                  <RichTextEditor
                    id={`pages[${pageIndex}].welcomeText`}
                    formik={formik}
                    value={values.pages[pageIndex].welcomeText}
                  />
                  <hr className="my-4" />
                  {page.questions.map((question, questionIndex, questions) => {
                    return (
                      <div
                        className="mt-1 space-y-4 flex flex-col mb-4 relative"
                        key={`page-${pageIndex}-question-${questionIndex}`}
                      >
                        {questions.length > 1 && (
                          <div className="absolute top-[11px] right-0">
                            <Tooltip
                              label={`Delete Question ${calculateQuestionNumber(
                                pageIndex,
                                questionIndex
                              )}`}
                            >
                              <button
                                onClick={() =>
                                  deleteQuestion(pageIndex, questionIndex)
                                }
                              >
                                <i className="fa-solid fa-circle-xmark text-red-500 text-lg cursor-pointer border border-white p-1 rounded-full" />
                              </button>
                            </Tooltip>
                          </div>
                        )}
                        <div
                          className="
                      text-sm font-bold text-gray-700
                    "
                        >
                          {t('question-with-number', {
                            questionNumber: calculateQuestionNumber(
                              pageIndex,
                              questionIndex
                            ),
                          })}
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                          <div className="flex flex-col col-span-2">
                            <label
                              htmlFor={`pages[${pageIndex}].questions[${questionIndex}].question`}
                              className="text-sm font-medium text-gray-700"
                            >
                              {t('Question')}
                            </label>
                            <input
                              type="text"
                              name={`pages[${pageIndex}].questions[${questionIndex}].question`}
                              id={`pages[${pageIndex}].questions[${questionIndex}].question`}
                              placeholder={t('Question')}
                              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md dark:bg-dark-primaryBackground dark:text-gray-200"
                              value={question.question}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {(errors?.pages?.[pageIndex] as any)?.questions?.[
                              questionIndex
                            ]?.question &&
                              (touched?.pages?.[pageIndex] as any)?.questions?.[
                                questionIndex
                              ]?.question && (
                                <ErrorMessage
                                  errors={
                                    (errors?.pages?.[pageIndex] as any)
                                      ?.questions?.[questionIndex]?.question
                                  }
                                  touched={touched.pages}
                                />
                              )}
                          </div>
                          <div className="col-span-1">
                            <div className="flex flex-col col-span-2">
                              <label
                                htmlFor={`pages[${pageIndex}].questions[${questionIndex}].question`}
                                className="text-sm font-medium text-gray-700"
                              >
                                {t('Type')}
                              </label>
                              <select
                                name={`pages[${pageIndex}].questions[${questionIndex}].type`}
                                id={`pages[${pageIndex}].questions[${questionIndex}].type`}
                                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md dark:bg-dark-primaryBackground dark:text-gray-200"
                                value={question.type}
                                onChange={(e) =>
                                  handleTypeChange(e, pageIndex, questionIndex)
                                }
                              >
                                <option value="short text">
                                  {t('Short Text')}
                                </option>
                                <option value="long text">
                                  {t('Long Text')}
                                </option>
                                <option value="select one">
                                  {t('Multiple Choice')}
                                </option>
                                <option value="select many">
                                  {t('Checkboxes')}
                                </option>
                                <option value="date">{t('Date')}</option>
                                <option value="scale">{t('Scale')}</option>
                                <option value="number">{t('Number')}</option>
                              </select>
                              <ErrorMessage
                                errors={
                                  (errors?.pages?.[pageIndex] as any)
                                    ?.questions?.[questionIndex]?.type
                                }
                                touched={touched.pages}
                              />
                            </div>
                            {question.type === QuestionTypeEnum.SCALE && (
                              <div className="col-span-2 mt-4">
                                <span className="text-sm font-medium">
                                  {t('Scale range')}
                                </span>
                                <div className="mt-2 flex items-center gap-3">
                                  <label className="flex items-center gap-2 text-sm">
                                    {t('From')}
                                    <input
                                      type="number"
                                      className="w-20 rounded-md border-2 border-gray-400 p-1"
                                      value={question.scale?.min ?? 1}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `pages[${pageIndex}].questions[${questionIndex}].scale.min`,
                                          Number(e.target.value)
                                        )
                                      }
                                    />
                                  </label>
                                  <label className="flex items-center gap-2 text-sm">
                                    {t('to')}
                                    <input
                                      type="number"
                                      className="w-20 rounded-md border-2 border-gray-400 p-1"
                                      value={question.scale?.max ?? 5}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `pages[${pageIndex}].questions[${questionIndex}].scale.max`,
                                          Number(e.target.value)
                                        )
                                      }
                                    />
                                  </label>
                                </div>
                                {/* The direction decides which end "top box"
                                    and "lowest scoring" mean, so getting it
                                    wrong inverts every report on the question
                                    rather than just relabelling it. */}
                                <label className="mt-3 flex items-center gap-2 text-sm">
                                  {t('Best answer is')}
                                  <select
                                    className="rounded-md border-gray-300 text-sm"
                                    value={
                                      question.scale?.direction ??
                                      'higherIsBetter'
                                    }
                                    onChange={(e) =>
                                      setFieldValue(
                                        `pages[${pageIndex}].questions[${questionIndex}].scale.direction`,
                                        e.target.value
                                      )
                                    }
                                  >
                                    <option value="higherIsBetter">
                                      {t('the highest number')}
                                    </option>
                                    <option value="lowerIsBetter">
                                      {t('the lowest number')}
                                    </option>
                                  </select>
                                </label>
                                <p className="mt-2 text-xs text-light-secondaryText">
                                  {t(
                                    'Respondents pick one number in this range. ' +
                                      'For Net Promoter Score use 0 to 10.'
                                  )}
                                </p>
                              </div>
                            )}
                            {!!question.options && (
                              <QuestionOptionsForm
                                questionIndex={questionIndex}
                                question={question}
                                touched={touched}
                                errors={errors}
                                pageIndex={pageIndex}
                                values={values}
                                setFieldValue={setFieldValue}
                              />
                            )}
                            <div className="flex flex-col col-span-2 mt-6 space-y-2">
                              <label
                                htmlFor={`pages[${pageIndex}].questions[${questionIndex}].isRequired`}
                                className="text-sm font-medium text-gray-700"
                              >
                                {t('Required')}
                              </label>
                              <Toggle
                                id={`pages[${pageIndex}].questions[${questionIndex}].isRequired`}
                                defaultToTrue
                                label={t('Set as required?')}
                                enabled={question?.isRequired}
                                setEnabled={(isRequired) =>
                                  setFieldValue(
                                    `pages[${pageIndex}].questions[${questionIndex}].isRequired`,
                                    isRequired
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                        <hr className="border-gray-300 dark:border-gray-700" />
                      </div>
                    );
                  })}
                  <div>
                    <button
                      onClick={() => addQuestion(pageIndex)}
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {t('Add Question')}
                    </button>
                  </div>
                </div>
                <ConfirmationModal
                  open={isConfirmDeleteModalOpen}
                  setOpen={setIsConfirmDeleteModalOpen}
                  text={'Are you sure you want to delete this page?'}
                  onConfirm={() => {
                    deletePage(pageToDelete);
                    setPageToDelete(-1);
                  }}
                />
              </fieldset>
            );
          })}
          <button
            onClick={addPage}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {t('Add Page')}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="flex flex-col col-span-1">
            <label
              htmlFor="completionButtonLabel"
              className="text-sm font-medium text-gray-700"
            >
              {t('Completion Button Label')}{' '}
              <Tooltip
                label={t(
                  'The text that will appear on the button users click to complete the form.'
                )}
              >
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="text-blue-400 cursor-pointer ml-1"
                />
              </Tooltip>
            </label>
            <input
              type="text"
              name="completionButtonLabel"
              id="completionButtonLabel"
              placeholder="Question"
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md dark:bg-dark-primaryBackground dark:text-gray-200"
              value={values.completionButtonLabel}
              onChange={handleChange}
            />
            <ErrorMessage
              errors={errors.completionButtonLabel}
              touched={touched.completionButtonLabel}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-between bg-white absolute w-full">
        <button
          type="button"
          onClick={onClose}
          className="mr-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          {t('Cancel')}
        </button>
        <div>
          <button
            type="submit"
            disabled={isSaving || !dirty}
            className={classNames(
              'inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md mr-4',
              {
                'text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500':
                  dirty,
                'text-gray-800 bg-gray-300 cursor-not-allowed': !dirty,
              }
            )}
          >
            {isSavingTemplate ? t('Saving...') : t('Save')}
          </button>
          <button
            onClick={() => setShouldCloseAfterSave(true)}
            disabled={isSaving || !dirty}
            type="submit"
            className={classNames(
              'inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md',
              {
                'text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500':
                  dirty,
                'text-gray-800 bg-gray-300 cursor-not-allowed': !dirty,
              }
            )}
          >
            {isSavingTemplate ? t('Saving...') : t('Save and Close')}
          </button>
        </div>
      </div>
      <TemplateSavedAlert />
    </form>
  );
}

export default AddTemplateForm;
